import { useState, useEffect, useCallback } from 'react';
import { Group, Badge, Button, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Wifi, WifiOff } from 'react-feather';
import { billcomApi } from '../../../../services/api/billcom';
import type { ConnectionStatus } from '../../../../services/api/billcom';

export function BillComConnectionStatus() {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [testing, setTesting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await billcomApi.getStatus();
      setStatus(data);
    } catch {
      setStatus({ connected: false, message: 'Failed to check status' });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      const result = await billcomApi.testConnection();
      setStatus(result);
      notifications.show({
        title: result.connected ? 'Connected' : 'Connection Failed',
        message: result.message,
        color: result.connected ? 'green' : 'red',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to test connection',
        color: 'red',
      });
    } finally {
      setTesting(false);
    }
  };

  const connected = status?.connected ?? false;

  return (
    <Group gap="sm">
      <Button
        variant="outline"
        color="gray"
        bg="white"
        size="xs"
        onClick={handleTestConnection}
        loading={testing}
        leftSection={!testing ? connected ? <Wifi size={14} /> : <WifiOff size={14} /> : undefined}
      >
        Test Connection
      </Button>
      {status === null ? (
        <Loader size="xs" />
      ) : (
        <Badge
          variant={connected ? 'light' : 'outline'}
          color={connected ? 'medleyGreen' : 'gray'}
          size="lg"
          radius="xl"
        >
          {connected ? 'Bill.com Connected' : 'Bill.com Not Connected'}
        </Badge>
      )}
    </Group>
  );
}
